import React, { useState, useEffect } from 'react';
import { contractService } from '../services/ContractService';

interface ContractInfo {
  address: string | null;
  network: string | null;
  isAvailable: boolean;
  userAddress: string | null;
  userBalance: string | null;
}

export const ContractTest: React.FC = () => {
  const [contractInfo, setContractInfo] = useState<ContractInfo>({
    address: null,
    network: null,
    isAvailable: false,
    userAddress: null,
    userBalance: null
  });
  const [testSafeAddress, setTestSafeAddress] = useState('0x742d35Cc6073C06c0B23b3F4Bb96d2C4b6F1C3D8');
  const [isRegistered, setIsRegistered] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    updateContractInfo();
  }, []);

  const updateContractInfo = async () => {
    try {
      const info: ContractInfo = {
        address: contractService.getContractAddress(),
        network: contractService.getCurrentNetwork(),
        isAvailable: contractService.isContractAvailable(),
        userAddress: await contractService.getCurrentAccount(),
        userBalance: null
      };

      if (info.userAddress) {
        info.userBalance = await contractService.getBalance(info.userAddress);
      }

      setContractInfo(info);
    } catch (err) {
      console.error('Error updating contract info:', err);
      setError('Failed to get contract information');
    }
  };

  const connectWallet = async () => {
    try {
      setLoading(true);
      setError(null);
      await contractService.connectWallet();
      await updateContractInfo();
      setSuccess('Wallet connected successfully!');
    } catch (err: any) {
      setError(err.message || 'Failed to connect wallet');
    } finally {
      setLoading(false);
    }
  };

  const checkSafeRegistration = async () => {
    if (!testSafeAddress) return;

    try {
      setLoading(true);
      setError(null);
      const registered = await contractService.isSafeRegistered(testSafeAddress);
      setIsRegistered(registered);
      setSuccess(`Safe registration status: ${registered ? 'Registered' : 'Not registered'}`);
    } catch (err: any) {
      setError(err.message || 'Failed to check Safe registration');
    } finally {
      setLoading(false);
    }
  };

  const registerSafe = async () => {
    if (!testSafeAddress) return;

    try {
      setLoading(true);
      setError(null);
      const tx = await contractService.registerSafe(testSafeAddress);
      setSuccess(`Registration transaction sent: ${tx.hash}`);

      // Wait for confirmation
      await tx.wait();
      setSuccess(`Safe registered successfully! Transaction: ${tx.hash}`);

      // Update registration status
      await checkSafeRegistration();
    } catch (err: any) {
      setError(err.message || 'Failed to register Safe');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold mb-4">🧪 Contract Integration Test</h2>

        {/* Contract Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="space-y-2">
            <h3 className="font-semibold">Contract Information</h3>
            <div className="text-sm space-y-1">
              <div><span className="font-medium">Network:</span> {contractInfo.network || 'Unknown'}</div>
              <div><span className="font-medium">Address:</span> {contractInfo.address || 'Not available'}</div>
              <div><span className="font-medium">Status:</span>
                <span className={`ml-2 px-2 py-1 rounded text-xs ${contractInfo.isAvailable ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  {contractInfo.isAvailable ? 'Available' : 'Not Available'}
                </span>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <h3 className="font-semibold">Wallet Information</h3>
            <div className="text-sm space-y-1">
              <div><span className="font-medium">Address:</span> {contractInfo.userAddress || 'Not connected'}</div>
              <div><span className="font-medium">Balance:</span> {contractInfo.userBalance ? `${contractInfo.userBalance} ETH` : 'N/A'}</div>
            </div>
          </div>
        </div>

        {/* Connect Wallet */}
        {!contractInfo.userAddress && (
          <div className="mb-6">
            <button
              onClick={connectWallet}
              disabled={loading}
              className="bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white px-4 py-2 rounded transition-colors"
            >
              {loading ? 'Connecting...' : 'Connect Wallet'}
            </button>
          </div>
        )}

        {/* Safe Registration Test */}
        {contractInfo.isAvailable && contractInfo.userAddress && (
          <div className="space-y-4">
            <h3 className="font-semibold">Safe Registration Test</h3>

            <div className="flex gap-2">
              <input
                type="text"
                value={testSafeAddress}
                onChange={(e) => setTestSafeAddress(e.target.value)}
                placeholder="Safe Address"
                className="flex-1 border border-gray-300 rounded px-3 py-2"
              />
              <button
                onClick={checkSafeRegistration}
                disabled={loading}
                className="bg-gray-500 hover:bg-gray-600 disabled:bg-gray-300 text-white px-4 py-2 rounded transition-colors"
              >
                {loading ? 'Checking...' : 'Check Registration'}
              </button>
            </div>

            {isRegistered !== null && (
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 rounded text-sm ${isRegistered ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                  {isRegistered ? 'Registered' : 'Not Registered'}
                </span>

                {!isRegistered && (
                  <button
                    onClick={registerSafe}
                    disabled={loading}
                    className="bg-green-500 hover:bg-green-600 disabled:bg-green-300 text-white px-3 py-1 rounded text-sm transition-colors"
                  >
                    {loading ? 'Registering...' : 'Register Safe'}
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Status Messages */}
        {error && (
          <div className="mt-4 p-3 bg-red-100 border border-red-300 text-red-800 rounded">
            <strong>Error:</strong> {error}
          </div>
        )}

        {success && (
          <div className="mt-4 p-3 bg-green-100 border border-green-300 text-green-800 rounded">
            <strong>Success:</strong> {success}
          </div>
        )}

        {/* Contract Links */}
        {contractInfo.address && contractInfo.network === 'sepolia' && (
          <div className="mt-6 p-4 bg-blue-50 rounded">
            <h4 className="font-semibold mb-2">Contract Links</h4>
            <div className="space-y-1">
              <a
                href={`https://sepolia.etherscan.io/address/${contractInfo.address}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 block"
              >
                📋 View on Etherscan
              </a>
              <a
                href={`https://sepolia.etherscan.io/address/${contractInfo.address}#code`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 block"
              >
                🔍 View Source Code
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};